/**
 * Live updates (task 2496): the rules, kept pure so they can be tested.
 * The connection itself is `composables/useLiveUpdates.ts`.
 *
 * The server only ever says "this changed, look again" ({ topic, warehouseId, gang }); the screen
 * then reads again through the request it already makes. Hints are not replayed: one sent while
 * the tablet was out of range is lost, so every (re)connect is followed by one read.
 */

export type LiveTopic = 'truck-loads' | 'pallets' | 'dock' | 'needs-attention'

export type LiveHint = {
  topic: LiveTopic | string
  warehouseId?: number | null
  gang?: string | null
}

/** What one screen wants to hear about. `gang: null` is every gang (the orders list shows them all). */
export type LiveInterest = {
  topics: LiveTopic[]
  gang: string | null
}

/** What the connection asks the hub for: one watch covering every open screen. */
export type LiveWatch = {
  topics: LiveTopic[]
  warehouseId: number | null
  gang: string | null
}

/** The tablets' and the server's spelling of a gang: case and spaces do not make another. */
export function normalizeGang(value: string | null | undefined): string | null {
  const squeezed = (value ?? '').replace(/\s+/g, '').toUpperCase()
  return squeezed === '' ? null : squeezed
}

/**
 * One watch for every interest. Topics are joined; a single screen wanting every gang makes the
 * watch every gang's (the others filter for themselves when they read).
 */
export function combineWatch(interests: LiveInterest[], warehouseId: number | null): LiveWatch {
  const topics = [...new Set(interests.flatMap(i => i.topics))].filter(t => t !== 'needs-attention').sort()
  const truckLoads = interests.filter(i => i.topics.includes('truck-loads'))
  const gangs = new Set(truckLoads.map(i => normalizeGang(i.gang)))
  const gang = gangs.size === 1 ? [...gangs][0] ?? null : null
  return { topics, warehouseId, gang }
}

export function sameWatch(a: LiveWatch | null, b: LiveWatch): boolean {
  return a !== null && a.warehouseId === b.warehouseId && a.gang === b.gang
    && a.topics.length === b.topics.length && a.topics.every((t, i) => t === b.topics[i])
}

/** Whether a hint is news for one screen. A hint with no gang is everyone's. */
export function hintConcerns(hint: LiveHint, interest: LiveInterest): boolean {
  if (!interest.topics.includes(hint.topic as LiveTopic)) return false
  if (hint.topic !== 'truck-loads') return true
  const wanted = normalizeGang(interest.gang)
  const said = normalizeGang(hint.gang ?? null)
  return wanted === null || said === null || wanted === said
}

/**
 * The wait before the next try to reconnect. Never "give up" (SignalR's default stops after
 * 0, 2, 10 and 30 seconds, which a forklift in a container outlasts): doubling from 1 s to a
 * cap of 60 s, each wait between half and all of it at random, so the tablets on one access
 * point do not all knock at the same moment when it comes back.
 */
export function retryDelayMs(previousRetryCount: number, random: () => number = Math.random): number {
  const capped = Math.min(60_000, 1_000 * 2 ** Math.min(previousRetryCount, 10))
  return Math.round(capped * (0.5 + random() * 0.5))
}

/** While the live connection holds, polling is only the safety net: slowed to a minute. */
export const LIVE_POLL_MS = 60_000

export function pollEvery(baseMs: number, live: boolean): number {
  return live ? Math.max(baseMs, LIVE_POLL_MS) : baseMs
}

/**
 * At most one read per `minGapMs` however many hints arrive: the first at once, the rest as one
 * more when the gap is over. `paused` holds it (a truck being loaded) and it runs once on resume.
 */
export function createRefetchGate(
  run: () => unknown,
  minGapMs: number,
  clock: { now: () => number, setTimeout: (fn: () => void, ms: number) => unknown, clearTimeout: (handle: unknown) => void } = {
    now: () => Date.now(),
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
  },
) {
  let last = -Infinity
  let timer: unknown = null
  let held = false

  function fire() {
    timer = null
    last = clock.now()
    void run()
  }

  return {
    /** A hint arrived, or the connection came back. */
    poke(paused = false) {
      if (paused) { held = true; return }
      if (timer !== null) return
      const wait = last + minGapMs - clock.now()
      if (wait <= 0) fire()
      else timer = clock.setTimeout(fire, wait)
    },
    /** The pause is over: what was held runs now (once). */
    resume() {
      if (!held) return
      held = false
      this.poke(false)
    },
    stop() {
      if (timer !== null) clock.clearTimeout(timer)
      timer = null
      held = false
    },
  }
}
